import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  useTheme,
} from "@mui/material";
import { AssignmentReturn as ReturnIcon } from "@mui/icons-material";
import { observer } from "mobx-react-lite";
import {
  SampleSirDebitLocationLine,
  SampleSirReturnSummary,
  StockIssueRequest,
} from "../../../../../models/StockIssueRequest/StockIssueRequest";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { useStores } from "../../../use-stores";
import SwageazyInventorySampleReturn from "../projects/swageazy-Inventory/SwageazyInventorySampleReturn";
import { sampleSirReturnShipmentBlockedMessage } from "../../../helpers/sampleSirReturnShipment.helper";

interface SirReturnProductsSectionProps {
  request: StockIssueRequest;
  canReturn: boolean;
  shipmentEligibleForReturn: boolean;
  onReturned: () => void;
}

function skuSummaryKey(productId: number, sku: string): string {
  return `${productId}::${String(sku ?? "").trim().toUpperCase()}`;
}

function debitLineRowKey(
  line: SampleSirDebitLocationLine,
  index: number,
): string {
  return `${line.product_id}::${line.sku}::${line.box_id}::${line.warehouse_id}::${index}`;
}

/**
 * Allocates SKU-level returned qty FIFO across debit lines (oldest debited first)
 * so each row shows issued / returned / pending for that box only.
 */
function allocateReturnQtyPerDebitLine(
  debitLocations: SampleSirDebitLocationLine[],
  returnSummary?: SampleSirReturnSummary,
): Map<string, { returned: number; pending: number }> {
  const returnedPoolBySku = new Map<string, number>();
  returnSummary?.lines.forEach((line) => {
    returnedPoolBySku.set(
      skuSummaryKey(line.product_id, line.sku),
      Number(line.returned ?? 0),
    );
  });

  const result = new Map<string, { returned: number; pending: number }>();
  const bySku = new Map<
    string,
    Array<{ rowKey: string; line: SampleSirDebitLocationLine }>
  >();

  debitLocations.forEach((line, index) => {
    const skuKey = skuSummaryKey(line.product_id, line.sku);
    const list = bySku.get(skuKey) ?? [];
    list.push({ rowKey: debitLineRowKey(line, index), line });
    bySku.set(skuKey, list);
  });

  for (const [skuKey, items] of Array.from(bySku.entries())) {
    let poolRemaining = returnedPoolBySku.get(skuKey) ?? 0;

    const sorted = [...items].sort((a, b) => {
      const aTime = a.line.debited_at
        ? new Date(a.line.debited_at).getTime()
        : 0;
      const bTime = b.line.debited_at
        ? new Date(b.line.debited_at).getTime()
        : 0;
      if (aTime !== bTime) return aTime - bTime;
      return String(a.line.box_id).localeCompare(String(b.line.box_id));
    });

    for (const item of sorted) {
      const issued = Number(item.line.quantity ?? 0);
      const returned = Math.min(issued, Math.max(poolRemaining, 0));
      poolRemaining -= returned;
      result.set(item.rowKey, {
        returned,
        pending: Math.max(issued - returned, 0),
      });
    }
  }

  return result;
}

const SirReturnProductsSection: React.FC<SirReturnProductsSectionProps> =
  observer(({ request, canReturn, shipmentEligibleForReturn, onReturned }) => {
    const theme = useTheme<Theme>();
    const { swageazyInventoryStore } = useStores();
    const { showError, showSuccess } = useNotification();

    const [returnPopupOpen, setReturnPopupOpen] = useState(false);

    const debitLocations = request.debit_locations ?? [];
    const returnSummary = request.return_summary;
    const totalPending = returnSummary?.total_pending ?? 0;
    const hasPending = totalPending > 0;

    const orderId = useMemo(
      () =>
        request.primary_order_id ??
        request.order_products_mapping.find((m) => m.order_id)?.order_id ??
        null,
      [request],
    );

    const qtyByDebitLine = useMemo(
      () => allocateReturnQtyPerDebitLine(debitLocations, returnSummary),
      [debitLocations, returnSummary],
    );

    const openReturnPopup = () => {
      if (!orderId) {
        showError("Error", "Order is not linked to this stock issue request.");
        return;
      }
      setReturnPopupOpen(true);
    };

    const closeReturnPopup = () => {
      setReturnPopupOpen(false);
    };

    const handleSampleReturn = () => {
      const formData = swageazyInventoryStore.sampleReturnRequestFormData;

      if (!formData?.order_id) {
        showError("Validation Error", "Please select a valid Order ID.");
        return;
      }

      if (!formData?.stock_issue_request_id) {
        showError(
          "Validation Error",
          "Stock issue request is required for this return.",
        );
        return;
      }

      const skuItems = formData?.sku_items || [];
      if (
        skuItems.length === 0 ||
        !skuItems.some(
          (item) => Boolean(item.selected) && Number(item.quantity) > 0,
        )
      ) {
        showError(
          "Validation Error",
          "Select at least one SKU and enter a returned quantity.",
        );
        return;
      }

      for (const item of skuItems) {
        if (!item.selected || !Number(item.quantity)) continue;
        const pending = Number(item.pending_quantity ?? 0);
        if (pending > 0 && Number(item.quantity) > pending) {
          showError(
            "Validation Error",
            `Returned qty for SKU ${item.sku} cannot exceed pending qty (${pending}).`,
          );
          return;
        }
      }

      if (!formData?.warehouse_id && !formData?.warehouse_name) {
        showError("Validation Error", "Please select a warehouse.");
        return;
      }

      if (!formData?.box_id?.trim()) {
        showError("Validation Error", "Please enter a valid Box ID.");
        return;
      }

      if (!formData?.current_rack) {
        showError("Validation Error", "Please select a valid Rack.");
        return;
      }

      swageazyInventoryStore.setIsLoading(true);
      swageazyInventoryStore
        .sampleReturn()
        .then(() => {
          showSuccess("Sample stock returned successfully.");
          closeReturnPopup();
          onReturned();
        })
        .catch((err: any) => {
          showError(
            "Error",
            err?.response?.data?.message ||
            err?.message ||
            "Failed to return stock. Please try again.",
          );
        })
        .finally(() => {
          swageazyInventoryStore.setIsLoading(false);
        });
    };

    if (debitLocations.length === 0) {
      return null;
    }

    return (
      <>
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              mb: 3,
              p: 2,
              backgroundColor: theme.palette.info.main + "08",
              borderRadius: 2,
              border: `2px solid ${theme.palette.info.main}20`,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.info.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <ReturnIcon sx={{ color: "white", fontSize: 20 }} />
              </Box>
              <Box>
                <CustomTypography
                  variant="heading5"
                  fontWeight={700}
                  color="info.main"
                  sx={{ mb: 1 }}
                >
                  SIR issued products
                </CustomTypography>
                <CustomTypography variant="body2" color="text.secondary">
                  Stock was debited from the locations below. Use{" "}
                  <strong>Return stock</strong> to credit inventory — select
                  SKUs, quantities, and where received samples are placed
                  (warehouse, rack, box).
                </CustomTypography>
                {returnSummary ? (
                  <CustomTypography
                    variant="body3"
                    color="text.secondary"
                    sx={{ mt: 0.5, fontSize: "0.75rem" }}
                  >
                    Returned {returnSummary.total_returned} /{" "}
                    {returnSummary.total_issued} · Pending{" "}
                    {returnSummary.total_pending}
                  </CustomTypography>
                ) : null}
                {hasPending && !shipmentEligibleForReturn ? (
                  <CustomTypography
                    variant="body3"
                    sx={{
                      mt: 1,
                      fontSize: "0.8125rem",
                      color: theme.palette.warning.dark,
                    }}
                  >
                    {sampleSirReturnShipmentBlockedMessage(
                      request.shipment_status_label,
                    )}
                  </CustomTypography>
                ) : null}
              </Box>
            </Box>

            {canReturn && hasPending && orderId ? (
              <AppButton
                variant="primary"
                label="Return stock"
                onClick={openReturnPopup}
                customStyles={{ minHeight: 40, fontSize: "0.875rem" }}
              />
            ) : null}
          </Box>

          <Card
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
              boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
            }}
          >
            <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
              <TableContainer
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  overflowX: "auto",
                }}
              >
                <Table size="medium" sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor:
                          theme.palette.customColors?.lightGreyColor,
                        "& th": { py: 1, height: 40 },
                      }}
                    >
                      <AppTableHeaderCell
                        minWidth="140px"
                        txtAlign="left"
                        sx={{ textAlign: "left", fontSize: 12, minWidth: 140 }}
                        fontSize="12px"
                      >
                        SKU
                      </AppTableHeaderCell>
                      <AppTableHeaderCell
                        minWidth="90px"
                        sx={{ textAlign: "center", fontSize: 12, minWidth: 90 }}
                        fontSize="12px"
                      >
                        Warehouse
                      </AppTableHeaderCell>
                      <AppTableHeaderCell
                        minWidth="72px"
                        sx={{ textAlign: "center", fontSize: 12, minWidth: 72 }}
                        fontSize="12px"
                      >
                        Rack
                      </AppTableHeaderCell>
                      <AppTableHeaderCell
                        minWidth="72px"
                        sx={{ textAlign: "center", fontSize: 12, minWidth: 72 }}
                        fontSize="12px"
                      >
                        Box
                      </AppTableHeaderCell>
                      <AppTableHeaderCell
                        minWidth="64px"
                        sx={{ textAlign: "center", fontSize: 12, minWidth: 64 }}
                        fontSize="12px"
                      >
                        Issued
                      </AppTableHeaderCell>
                      <AppTableHeaderCell
                        minWidth="72px"
                        sx={{ textAlign: "center", fontSize: 12, minWidth: 72 }}
                        fontSize="12px"
                      >
                        Returned
                      </AppTableHeaderCell>
                      <AppTableHeaderCell
                        minWidth="72px"
                        sx={{ textAlign: "center", fontSize: 12, minWidth: 72 }}
                        fontSize="12px"
                      >
                        Pending
                      </AppTableHeaderCell>
                      <AppTableHeaderCell
                        minWidth="120px"
                        sx={{ textAlign: "center", fontSize: 12, minWidth: 120 }}
                        fontSize="12px"
                      >
                        Debited on
                      </AppTableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {debitLocations.map((line, index) => {
                      const rowKey = debitLineRowKey(line, index);
                      const lineQty = qtyByDebitLine.get(rowKey) ?? {
                        returned: 0,
                        pending: Number(line.quantity ?? 0),
                      };
                      const isFullyReturned =
                        Boolean(returnSummary) && lineQty.pending === 0;

                      return (
                      <TableRow
                        key={rowKey}
                        hover
                        sx={{
                          backgroundColor: isFullyReturned
                            ? theme.palette.success.main + "12"
                            : undefined,
                          "&:hover": {
                            backgroundColor: isFullyReturned
                              ? theme.palette.success.main + "1A"
                              : undefined,
                          },
                        }}
                      >
                        <AppTableRowCell
                          align="left"
                          sx={{ fontSize: "12px", wordBreak: "break-all" }}
                        >
                          {line.sku}
                        </AppTableRowCell>
                        <AppTableRowCell
                          sx={{ textAlign: "center", fontSize: "12px" }}
                        >
                          {line.warehouse_name || line.warehouse_id}
                        </AppTableRowCell>
                        <AppTableRowCell
                          sx={{ textAlign: "center", fontSize: "12px" }}
                        >
                          {line.rack || "—"}
                        </AppTableRowCell>
                        <AppTableRowCell
                          sx={{
                            textAlign: "center",
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          {line.box_id}
                        </AppTableRowCell>
                        <AppTableRowCell
                          sx={{
                            textAlign: "center",
                            fontSize: "12px",
                            fontWeight: 700,
                          }}
                        >
                          {line.quantity}
                        </AppTableRowCell>
                        <AppTableRowCell
                          sx={{
                            textAlign: "center",
                            fontSize: "12px",
                            fontWeight: 700,
                            color:
                              lineQty.returned > 0
                                ? theme.palette.success.main
                                : undefined,
                          }}
                        >
                          {returnSummary ? lineQty.returned : "—"}
                        </AppTableRowCell>
                        <AppTableRowCell
                          sx={{
                            textAlign: "center",
                            fontSize: "12px",
                            fontWeight: 700,
                            color:
                              lineQty.pending > 0
                                ? theme.palette.warning.dark
                                : theme.palette.success.main,
                          }}
                        >
                          {returnSummary ? lineQty.pending : "—"}
                        </AppTableRowCell>
                        <AppTableRowCell
                          sx={{ textAlign: "center", fontSize: "12px" }}
                        >
                          {line.debited_at ? formatDate(line.debited_at) : "—"}
                        </AppTableRowCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>

        {orderId ? (
          <SwageazyInventorySampleReturn
            open={returnPopupOpen}
            onClose={closeReturnPopup}
            onSave={handleSampleReturn}
            prefill={{
              orderId,
              stockIssueRequestId: request.id,
              lockSelection: true,
            }}
          />
        ) : null}
      </>
    );
  });

export default SirReturnProductsSection;
